import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { storyService, photoService } from '../../services/api';
import { format } from 'date-fns';
import {
  FiBook, FiPlus, FiArrowLeft, FiEdit2, FiTrash2, FiImage, FiX
} from 'react-icons/fi';
import './Stories.css';

const STORY_TYPES = [
  { value: 'memory', label: '💭 Memory' },
  { value: 'biography', label: '📖 Life Story' },
  { value: 'travel', label: '✈️ Travel' },
  { value: 'milestone', label: '🌟 Milestone' },
  { value: 'tribute', label: '❤️ Tribute' },
  { value: 'recipe', label: '🍳 Recipe Story' },
  { value: 'other', label: '📝 Other' },
];

const Stories = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStory, setSelectedStory] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);

  const fetchStories = useCallback(async () => {
    try {
      setLoading(true);
      const response = await storyService.getAllStories();
      if (response.success) {
        setStories(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch stories:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStories();
  }, [fetchStories]);

  useEffect(() => {
    setSelectedStory(null);
    setShowCreateForm(false);
    setShowEditForm(false);
    fetchStories();
  }, [location.key, fetchStories]);

  const handleViewStory = async (storyId) => {
    try {
      const response = await storyService.getStory(storyId);
      if (response.success) {
        setSelectedStory(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch story:', err);
    }
  };

  const handleDelete = async (storyId) => {
    if (!window.confirm('Delete this story? This cannot be undone.')) return;
    try {
      const response = await storyService.deleteStory(storyId);
      if (response.success) {
        setStories(prev => prev.filter(s => s.storyID !== storyId));
        setSelectedStory(null);
      }
    } catch (err) {
      console.error('Failed to delete story:', err);
    }
  };

  const handleStoryCreated = (newStory) => {
    setStories(prev => [newStory, ...prev]);
    setShowCreateForm(false);
    setSelectedStory(newStory);
  };

  const handleStoryUpdated = (updatedStory) => {
    setStories(prev => prev.map(s => s.storyID === updatedStory.storyID ? updatedStory : s));
    setSelectedStory(updatedStory);
    setShowEditForm(false);
  };

  const getStoryTypeEmoji = (type) => {
    const found = STORY_TYPES.find(t => t.value === type);
    return found ? found.label.split(' ')[0] : '📝';
  };

  const canEdit = (story) => user?.memberID === story.createdByMemberID || user?.isAdmin;

  // Detail View
  if (selectedStory && !showEditForm) {
    return (
      <div className="stories-page">
        <div className="container">
          <button className="back-btn" onClick={() => setSelectedStory(null)}>
            <FiArrowLeft /> Back to Stories
          </button>

          <StoryDetail
            story={selectedStory}
            canEdit={canEdit(selectedStory)}
            onEdit={() => setShowEditForm(true)}
            onDelete={() => handleDelete(selectedStory.storyID)}
            getStoryTypeEmoji={getStoryTypeEmoji}
          />
        </div>
      </div>
    );
  }

  // Create/Edit Form
  if (showCreateForm || showEditForm) {
    return (
      <div className="stories-page">
        <div className="container">
          <StoryForm
            story={showEditForm ? selectedStory : null}
            onSave={showEditForm ? handleStoryUpdated : handleStoryCreated}
            onCancel={() => { setShowCreateForm(false); setShowEditForm(false); }}
          />
        </div>
      </div>
    );
  }

  // List View
  return (
    <div className="stories-page">
      <div className="container">
        <div className="page-header">
          <h1><FiBook className="page-header-icon" /> Family Stories</h1>
          <p className="page-subtitle">Share memories, milestones, and life stories</p>
        </div>

        <div className="stories-toolbar">
          <button className="btn btn-primary" onClick={() => setShowCreateForm(true)}>
            <FiPlus /> Write a Story
          </button>
        </div>

        {loading ? (
          <div className="loading-state">
            <span className="spinner spinner-lg"></span>
            <p>Loading stories...</p>
          </div>
        ) : stories.length === 0 ? (
          <div className="empty-state card">
            <div className="empty-state-icon">📖</div>
            <h3 className="empty-state-title">No stories yet</h3>
            <p>Share a memory or life story with the family!</p>
            <button className="btn btn-primary" onClick={() => setShowCreateForm(true)}>
              <FiPlus /> Write a Story
            </button>
          </div>
        ) : (
          <div className="stories-list">
            {stories.map(story => (
              <div
                key={story.storyID}
                className="story-card card"
                onClick={() => handleViewStory(story.storyID)}
              >
                {story.photoURL && (
                  <div className="story-card-photo">
                    <img src={story.photoURL} alt={story.title} />
                  </div>
                )}
                <div className="story-card-body">
                  <div className="story-card-header">
                    <h3 className="story-card-title">
                      {getStoryTypeEmoji(story.storyType)} {story.title}
                    </h3>
                  </div>
                  <p className="story-card-preview">{story.contentPreview}</p>
                  <div className="story-card-meta">
                    <span>By {story.createdByMember?.fullName}</span>
                    <span>·</span>
                    <span>{format(new Date(story.createdDate + 'Z'), 'MMM d, yyyy')}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// STORY DETAIL
// ============================================
const StoryDetail = ({ story, canEdit, onEdit, onDelete, getStoryTypeEmoji }) => {
  return (
    <article className="story-detail card">
      <div className="card-body">
        <div className="story-detail-header">
          <h1>{getStoryTypeEmoji(story.storyType)} {story.title}</h1>

          {canEdit && (
            <div className="story-detail-actions">
              <button className="btn btn-secondary btn-sm" onClick={onEdit}>
                <FiEdit2 /> Edit
              </button>
              <button className="btn btn-ghost btn-sm" onClick={onDelete}>
                <FiTrash2 /> Delete
              </button>
            </div>
          )}
        </div>

        <div className="story-detail-meta">
          <span>By <Link to={`/member/${story.createdByMemberID}`}>{story.createdByMember?.fullName}</Link></span>
          <span>·</span>
          <span>{format(new Date(story.createdDate + 'Z'), 'MMMM d, yyyy')}</span>
          {story.storyDate && (
            <>
              <span>·</span>
              <span>Story from {format(new Date(story.storyDate.split('T')[0] + 'T12:00:00'), 'MMMM yyyy')}</span>
            </>
          )}
        </div>

        {story.photoURL && (
          <div className="story-detail-photo">
            <img src={story.photoURL} alt={story.title} />
          </div>
        )}

        <div className="story-detail-content">
          {story.content.split('\n').map((paragraph, i) => (
            paragraph.trim() ? <p key={i}>{paragraph}</p> : null
          ))}
        </div>
      </div>
    </article>
  );
};

// ============================================
// STORY FORM
// ============================================
const StoryForm = ({ story, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: story?.title || '',
    content: story?.content || '',
    storyType: story?.storyType || 'memory',
    storyDate: story?.storyDate ? story.storyDate.split('T')[0] : '',
    photoURL: story?.photoURL || '',
    visibility: story?.visibility || 'family'
  });
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingPhoto(true);
      const fd = new FormData();
      fd.append('file', file);
      const response = await photoService.uploadPhoto(fd);
      if (response.success) {
        setFormData(prev => ({ ...prev, photoURL: response.data.fileURL }));
      }
    } catch (err) {
      setError('Failed to upload photo.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.content.trim()) {
      setError('Title and content are required.');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const payload = {
        ...formData,
        storyDate: formData.storyDate ? new Date(formData.storyDate).toISOString() : null
      };

      let response;
      if (story) {
        response = await storyService.updateStory(story.storyID, payload);
      } else {
        response = await storyService.createStory(payload);
      }

      if (response.success) {
        onSave(response.data);
      } else {
        setError(response.message || 'Failed to save story.');
      }
    } catch (err) {
      setError('Failed to save story.');
      console.error('Failed to save story:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="story-form card">
      <div className="card-body">
        <h2>{story ? 'Edit Story' : 'Write a Story'}</h2>
        {error && <div className="form-error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Title *</label>
            <input
              type="text"
              name="title"
              className="form-input"
              placeholder="Give your story a title"
              value={formData.title}
              onChange={handleChange}
            />
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Story Type</label>
              <select name="storyType" className="form-input" value={formData.storyType} onChange={handleChange}>
                {STORY_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Date (Optional)</label>
              <input
                type="date"
                name="storyDate"
                className="form-input"
                value={formData.storyDate}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Cover Photo (Optional)</label>
            {formData.photoURL ? (
              <div className="story-photo-preview">
                <img src={formData.photoURL} alt="Cover" />
                <button type="button" className="story-photo-remove" onClick={() => setFormData(prev => ({ ...prev, photoURL: '' }))}>
                  <FiX />
                </button>
              </div>
            ) : (
              <label className="story-photo-upload">
                <input type="file" accept="image/*" onChange={handlePhotoUpload} hidden />
                <FiImage />
                <span>{uploadingPhoto ? 'Uploading...' : 'Add a photo'}</span>
              </label>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Your Story *</label>
            <textarea
              name="content"
              className="form-input"
              rows={12}
              placeholder="Share your story... use blank lines to separate paragraphs."
              value={formData.content}
              onChange={handleChange}
            />
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-ghost" onClick={onCancel}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : (story ? 'Update Story' : 'Publish Story')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Stories;